<template>
  <v-card class="fill-height" outlined>
    <template v-if="entity">
      <v-card-title>
        {{ $t("labels.section-construction-association-title") }}
      </v-card-title>

      <v-card-text class="d-flex align-center px-4 py-2">
        <v-avatar size="32" :color="entity.data.color" class="mr-3">
          <v-icon size="20">{{ entity.data.icon }}</v-icon>
        </v-avatar>

        <div class="mr-3">
          <div class="text-subtitle-1 font-weight-medium">
            {{ entity.data.aliasName }}
          </div>
          <div class="text-caption text-grey">{{ entity.data.category }}</div>
        </div>

        <DialogEntityProperty
          :name="entity.data.aliasName"
          :properties="entity.data.propertiesValue"
        />
      </v-card-text>

      <v-card-text class="pt-0">
        <v-tabs v-model="tab" class="mt-2" color="secondary" grow>
          <v-tab
            :disabled="
              (tab == 'kafka' && !!bindingsKafka) ||
              (tab == 'rest-api' && !!bindingsRestApi) ||
              (tab == 'coap' && !!bindingsCoAP)
            "
            value="mqtt"
            >{{ $t("labels.section-construction-association-tab.mqtt") }}</v-tab
          >
          <v-tab
            :disabled="
              (tab == 'kafka' && !!bindingsKafka) ||
              (tab == 'mqtt' && !!bindingsMQTT) ||
              (tab == 'coap' && !!bindingsCoAP)
            "
            value="rest-api"
            >{{
              $t("labels.section-construction-association-tab.rest-api")
            }}</v-tab
          >
          <v-tab
            :disabled="
              (tab == 'mqtt' && !!bindingsMQTT) ||
              (tab == 'rest-api' && !!bindingsRestApi) ||
              (tab == 'coap' && !!bindingsCoAP)
            "
            value="kafka"
            >{{
              $t("labels.section-construction-association-tab.kafka")
            }}</v-tab
          >
          <v-tab
            :disabled="
              (tab == 'kafka' && !!bindingsKafka) ||
              (tab == 'mqtt' && !!bindingsMQTT) ||
              (tab == 'rest-api' && !!bindingsRestApi)
            "
            value="coap"
            >{{ $t("labels.section-construction-association-tab.coap") }}</v-tab
          >
        </v-tabs>

        <v-tabs-window v-model="tab" class="height-tabs-window-custom">
          <v-tabs-window-item value="mqtt">
            <v-container class="pa-4">
              <MQTTForm
                ref="mqttForm"
                :bindings="bindingsMQTT"
                :isOwner="isOwner"
                @dataSourceAssociation="dataSourceAssociation"
                @resetFields="resetFields"
              ></MQTTForm>
            </v-container>
          </v-tabs-window-item>

          <v-tabs-window-item value="rest-api">
            <v-container class="pa-4">
              <RestApiForm
                ref="restApiForm"
                :bindings="bindingsRestApi"
                :isOwner="isOwner"
                @dataSourceAssociation="dataSourceAssociation"
                @resetFields="resetFields"
                @openDialogJsonProperty="
                  (e) => emit('openDialogJsonProperty', e)
                "
              ></RestApiForm>
            </v-container>
          </v-tabs-window-item>

          <v-tabs-window-item value="kafka">
            <v-container class="pa-4">
              <KafkaForm
                ref="kafkaForm"
                :bindings="bindingsKafka"
                :isOwner="isOwner"
                @dataSourceAssociation="dataSourceAssociation"
                @resetFields="resetFields"
              ></KafkaForm>
            </v-container>
          </v-tabs-window-item>

          <v-tabs-window-item value="coap">
            <v-container class="pa-4">
              <CoAPForm
                ref="coAPForm"
                :bindings="bindingsMQTT"
                :isOwner="isOwner"
                @dataSourceAssociation="dataSourceAssociation"
                @resetFields="resetFields"
              ></CoAPForm>
            </v-container>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-card-text>
    </template>

    <template v-else>
      <v-card-text
        class="fill-height d-flex flex-column align-center justify-center text-center"
      >
        <v-icon size="48" color="grey">mdi-database-edit</v-icon>
        <div class="text-h6 mt-2">{{ $t("labels.no-entity-selected") }}</div>
        <div class="text-body-2 text-grey mt-1">
          {{ $t("labels.select-entity-to-configure") }}
        </div>
      </v-card-text>
    </template>
  </v-card>
</template>

<script setup>
import { ref, defineEmits, watch, useTemplateRef, defineExpose } from "vue";
import MQTTForm from "./MQTTForm.vue";
import KafkaForm from "./KafkaForm.vue";
import RestApiForm from "./RestApiForm.vue";
import CoAPForm from "./CoAPForm.vue";
import DialogEntityProperty from "./DialogEntityProperty.vue";

const props = defineProps(["entity", "isOwner"]);

const emit = defineEmits([
  "dataSourceAssociation",
  "resetFields",
  "openDialogJsonProperty",
]);

const tab = ref("");
const bindingsKafka = ref(undefined);
const bindingsRestApi = ref(undefined);
const bindingsMQTT = ref(undefined);
const bindingsCoAP = ref(undefined);

const mqttForm = useTemplateRef("mqttForm");
const kafkaForm = useTemplateRef("kafkaForm");
const restApiForm = useTemplateRef("restApiForm");
const coAPForm = useTemplateRef("coAPForm");

defineExpose({
  restApiForm,
});

watch(
  () => props.entity,
  (newVal) => {
    if (newVal?.data?.bindings?.type) {
      tab.value = newVal.data.bindings.type;
      if (tab.value == "mqtt") {
        bindingsMQTT.value = newVal.data.bindings;
        resetKafka();
        resetRestApi();
        resetCoAP();
      } else if (tab.value == "rest-api") {
        bindingsRestApi.value = newVal.data.bindings;
        resetKafka();
        resetMQTT();
        resetCoAP();
      } else if (tab.value == "kafka") {
        bindingsKafka.value = newVal.data.bindings;
        resetMQTT();
        resetRestApi();
        resetCoAP();
      } else if (tab.value == "coap") {
        bindingsCoAP.value = newVal.data.bindings;
        resetKafka();
        resetMQTT();
        resetRestApi();
      }
    } else {
      tab.value = "mqtt";
      resetKafka();
      resetMQTT();
      resetRestApi();
      resetCoAP();
    }
  },
);

function resetKafka() {
  bindingsKafka.value = undefined;
  kafkaForm.value?.resetForm();
}

function resetMQTT() {
  bindingsMQTT.value = undefined;
  mqttForm.value?.resetForm();
}

function resetRestApi() {
  bindingsRestApi.value = undefined;
  restApiForm.value?.resetForm();
}

function resetCoAP() {
  bindingsCoAP.value = undefined;
  coAPForm.value?.resetForm();
}

function dataSourceAssociation(data) {
  emit("dataSourceAssociation", data);
}

function resetFields() {
  emit("resetFields");
  resetKafka();
  resetMQTT();
  resetRestApi();
  resetCoAP();
}
</script>

<style scoped>
.height-tabs-window-custom {
  max-height: calc(100vh - 360px);
  overflow-y: auto;
}
</style>
